import React, { useState, useEffect } from "react";
import "../styles/home.css";
import "../styles/blogs.css";

export default function BlogTwo() {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      root.classList.add("home-root");
      root.style.flexGrow = "1";
    }

    const handleScroll = () => {
      setSticky(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="home-page">

      <header className={`navbar ${sticky ? "sticky" : ""}`}>
        <div className="nav-container">
          <div className="nav-left">
            <a href="/" className="logo">Roam</a>
           
          </div>
          <div className="nav-right">
            <a href="/login" className="btn-transparent" target="_blank" rel="noreferrer">Login</a>
            <a href="/register" className="btn-white" target="_blank" rel="noreferrer">Sign Up</a>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <section className="blog-wrapper section-features">
        <div className="blog-container">
          <div className="blog-body-text">

            {/* Header Block */}
            <h1 className="blog-main-title">
              Travel Nurse Pay Packages Explained: Taxable Pay, Stipends, and Estimated Take-Home Pay
            </h1>
<p className="blog-lead-paragraph" style={{ fontStyle: 'italic', opacity: '0.80', fontWeight: '300' }}>
                Travel healthcare compensation is easier to compare once each part of the package has a name, a purpose, and a realistic place in the calculation.
            </p>

            {/* Intro */}
            <h3 className="blog-section-title">Why travel healthcare pay feels more complicated than it should</h3>
            <p className="blog-paragraph">
              A permanent staff position may be presented as an hourly wage or annual salary. A travel healthcare contract can include taxable hourly wages, housing and meal allowances, travel reimbursement, bonuses, overtime, shift differentials, insurance, and one-time expenses. Recruiters may combine these into a weekly gross amount or a blended hourly rate, which is convenient for advertising but less useful for understanding the offer.
            </p>
            <p className="blog-paragraph">
              The solution is not to memorize one perfect formula. It is to separate the package into components and compare the same components across every offer.
            </p>

            {/* Taxable Hourly Pay */}
            <h3 className="blog-section-title">Taxable hourly pay</h3>
            <p className="blog-paragraph">
              The taxable hourly rate is the wage paid for your regular working hours. Federal income tax withholding, Social Security and Medicare taxes, and applicable state or local withholding may be taken from taxable wages. Your actual withholding depends on your pay, location, payroll setup, and tax information.
            </p>
            <p className="blog-paragraph">
              Do not confuse the taxable rate with the blended rate. A blended rate usually spreads the value of stipends or other payments across the scheduled hours. It can help summarize an offer, but it does not show how much of the package is treated as wages.
            </p>

            {/* Housing, Meals, allowances */}
            <h3 className="blog-section-title">Housing, meals, and incidental allowances</h3>
            <p className="blog-paragraph">
              Many travel healthcare packages include payments intended to cover lodging, meals, and incidental expenses while working away from the worker's tax home. These payments are often described casually as "tax-free stipends," but that label is not a guarantee.
            </p>
            <p className="blog-paragraph">
              The IRS distinguishes accountable and nonaccountable reimbursement arrangements. Under accountable-plan rules, the expenses must have a business connection, the worker must adequately account for them within a reasonable period, and excess reimbursement generally must be returned. IRS guidance also explains that per diem amounts above substantiated or federal limits can become taxable wages.
            </p>
            <p className="blog-paragraph">
              The practical lesson is simple: do not assume that a payment is nontaxable solely because a recruiter calls it a stipend. Confirm how the agency treats the payment and obtain qualified tax advice for your own circumstances.
            </p>

            {/* GSA Rates */}
            <h3 className="blog-section-title">What GSA per diem rates do - and do not - mean</h3>
            <p className="blog-paragraph">
              The U.S. General Services Administration publishes federal per diem rates for lodging and meals and incidental expenses. Federal agencies use those rates to reimburse employees traveling on official business. Private employers may also use federal rates when administering qualifying reimbursement arrangements.
            </p>
            <p className="blog-paragraph">
              A GSA rate is not a promise that a private staffing company must pay you that amount. It is also not proof, by itself, that any payment you receive is nontaxable. The employment arrangement, tax-home rules, substantiation, assignment facts, and employer plan all matter.
            </p>

            {/* Tax Home */}
            <h3 className="blog-section-title">Your tax home matters</h3>
            <p className="blog-paragraph">
              For federal travel-expense purposes, the IRS generally treats your tax home as your regular place of business or post of duty, not automatically the place where your family lives. Publication 463 explains the rules for temporary assignments, indefinite assignments, and workers who do not have a regular place of business.
            </p>
            <p className="blog-paragraph">
              The IRS states that an assignment expected to last more than one year is generally considered indefinite. When a work location becomes indefinite, it may become the worker's tax home, changing the treatment of travel expenses. These determinations depend heavily on facts, so broad internet slogans are poor substitutes for individual advice.
            </p>

            {/* Overtime */}
            <h3 className="blog-section-title">Overtime and shift differentials</h3>
            <p className="blog-paragraph">
              For workers covered by the Fair Labor Standards Act, overtime is generally required after 40 hours in a workweek at no less than one and one-half times the regular rate. The regular rate may include more than the base hourly wage. The Department of Labor specifically identifies certain bonuses and shift differentials as compensation that may need to be included in the calculation.
            </p>
            <p className="blog-paragraph">
              Some qualifying hospitals and residential care establishments can use an 8-and-80 system with a prior agreement, paying overtime for hours over eight in a day and over 80 in a fixed 14-day period. The contract and payroll method should explain which system applies.
            </p>

            {/* Pay Types */}
            <h3 className="blog-section-title">Gross pay, take-home pay, and spendable pay are different</h3>
            <ul className="blog-list">
              <li><strong>Gross pay:</strong> The full amount before taxes, deductions, and personal assignment expenses.</li>
              <li><strong>Estimated take-home pay:</strong> Gross pay minus estimated payroll taxes and benefit or payroll deductions.</li>
              <li><strong>Estimated spendable pay:</strong> Estimated take-home pay minus housing, commuting, travel, duplicate living expenses, and other costs of completing the assignment.</li>
            </ul>
            <p className="blog-paragraph">
              These numbers answer different questions. Gross pay describes the package. Estimated take-home pay describes the paycheck. Estimated spendable pay helps you decide whether the assignment improves your actual financial position.
            </p>

            {/* Package Breakdown */}
            <h3 className="blog-section-title">A sample weekly package breakdown</h3>
            <p className="blog-paragraph">
              Consider a hypothetical 36-hour contract with the following weekly amounts:
            </p>
            <ul className="blog-list">
              <li>Taxable wages: $28 per hour x 36 hours = $1,008.</li>
              <li>Housing allowance: $850.</li>
              <li>Meals and incidentals allowance: $450.</li>
              <li><strong>Gross weekly package:</strong> $1,008 + $850 + $450 = $2,308.</li>
            </ul>
            <p className="blog-paragraph">
              A blended rate would be $2,308 / 36 = approximately $64.11 per scheduled hour. That does not mean the worker earns a taxable wage of $64.11 per hour. It is only a way to express the total package across scheduled hours.
            </p>
            <p className="blog-paragraph">
              To estimate take-home pay, taxes should be calculated against the amounts treated as taxable wages using the worker's actual location and profile. Then housing and other assignment expenses should be subtracted separately. This is why a calculator that keeps each component visible is more informative than one flat percentage.
            </p>

            {/* Questions Section */}
            <h3 className="blog-section-title">Questions to ask before accepting the package</h3>
            <ol className="blog-paragraph" style={{ paddingLeft: '24px', listStyleType: 'decimal' }}>
              <li style={{ marginBottom: '12px' }}>Which payments are treated as taxable wages?</li>
              <li style={{ marginBottom: '12px' }}>What documentation does the agency require for reimbursements?</li>
              <li style={{ marginBottom: '12px' }}>Are the housing and meal amounts reduced for missed or canceled shifts?</li>
              <li style={{ marginBottom: '12px' }}>How are overtime, extra hours, holidays, call-back work, and shift differentials calculated?</li>
              <li style={{ marginBottom: '12px' }}>Are the scheduled hours guaranteed?</li>
              <li style={{ marginBottom: '12px' }}>When does health insurance begin, and what is the employee cost?</li>
              <li style={{ marginBottom: '12px' }}>Which licensing, travel, certification, and onboarding expenses are reimbursed?</li>
              <li style={{ marginBottom: '12px' }}>What happens to compensation if the facility cancels the contract early?</li>
            </ol>

            {/* Call To Action */}
            <hr className="blog-divider" />
            <h3 className="blog-section-title">Make the package understandable before making it exciting</h3>
            <p className="blog-paragraph">
              A good contract decision should survive a detailed breakdown. Roam Pays allows traveling medical professionals to enter the location, hourly rate, overtime, stipends, meal and housing allowances, and personal tax information, then compare estimated results across saved contracts. The app does not replace professional advice, but it can replace the pile of mismatched notes and recruiter screenshots that usually passes for a comparison system.
            </p>
            <p className="blog-paragraph" style={{ color: '#ffffff' }}>
              Break down your next pay package with Roam Pays. Compare taxable wages, stipends, overtime, estimated taxes, and contract costs before you sign.
            </p>

            {/* Footer Notes */}
            <hr className="blog-divider" />
            <p className="blog-disclaimer">
              Disclaimer: This article provides general educational information and is not tax, legal, accounting, or employment advice. Tax treatment and payroll calculations depend on individual facts and current law.
            </p>

            <h5 className="blog-sources-title">Sources</h5>
            <ul className="blog-sources-list">
              <li>IRS Publication 463, Travel, Gift, and Car Expenses</li>
              <li>IRS Publication 15, Employer's Tax Guide</li>
              <li>GSA Per Diem Rates and Travel Resources</li>
              <li>U.S. Department of Labor Fact Sheet #54, Health Care Overtime</li>
              <li>Roam Pays App Store listing</li>
            </ul>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" id="contact">
        <div className="container footer-grid">
          <div className="footer-left flex-grow-1">
            <div className="footer-brand">
              <a href="/" className="logo">Roam</a>
              <p className="color-secondary fs-16">
                Per Diem Calculator for <br /> Travel Healthcare Professionals
              </p>
              <div className="social-icons">
                <a href="#" title="Dribble">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M17.3569 21.2708C19.8824 19.6656 21.6391 16.9587 21.9505 13.8265C19.3663 13.8357 17.4497 13.9085 15.557 14.4783C16.3697 16.5938 16.9709 18.8301 17.3569 21.2708ZM13.6949 15.2082C11.6566 16.1964 9.37679 17.923 6.24199 21.0021C7.86997 22.1506 9.8562 22.8252 12 22.8252C13.2216 22.8252 14.3921 22.6061 15.4743 22.2052C15.1192 19.675 14.5276 17.3787 13.6949 15.2082ZM14.7783 12.626C17.0449 11.9078 19.276 11.8336 21.9507 11.826C21.7731 10.0355 21.1232 8.38385 20.1273 6.99746C18.1435 8.53194 16.1205 9.77561 13.8293 10.7132C14.1665 11.343 14.4829 11.9797 14.7783 12.626ZM11.9271 11.4C12.2809 12.0474 12.6105 12.7008 12.9162 13.3635C10.5333 14.4996 8.01868 16.4408 4.72628 19.6877C3.0361 17.8968 2 15.482 2 12.8252L2 12.8168C5.92271 12.7502 9.12157 12.2843 11.9271 11.4ZM12.8277 8.96034C14.9834 8.10829 16.8941 6.95835 18.8014 5.49428C17.0169 3.83795 14.6267 2.8252 12 2.8252C10.8851 2.8252 9.81273 3.00766 8.81125 3.34436C10.3405 5.2239 11.6804 7.07118 12.8277 8.96034ZM6.92979 4.20401C8.44524 6.03834 9.76388 7.82028 10.889 9.62555C8.46507 10.3377 5.66455 10.7384 2.20261 10.8126C2.77743 7.99949 4.53338 5.61641 6.92979 4.20401Z" fill="#A2ABB8" />
                  </svg>
                </a>
                <a href="#" title="X">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path d="M9.08612 15.5389C8.89301 15.2915 8.52236 15.2811 8.31569 15.5173L4.24744 20.1667C3.88375 20.5823 3.92587 21.2141 4.34151 21.5778C4.75715 21.9415 5.38891 21.8993 5.75259 21.4837L9.69475 16.9784C9.85271 16.7979 9.86021 16.5306 9.71261 16.3415L9.08612 15.5389Z" fill="#A2ABB8" />
                    <path d="M15.6897 10.127C15.483 10.3632 15.1124 10.3528 14.9193 10.1054L14.2928 9.30279C14.1452 9.11369 14.1527 8.84642 14.3107 8.66588L18.2474 4.1667C18.6111 3.75107 19.2429 3.70895 19.6585 4.07263C20.0742 4.43631 20.1163 5.06808 19.7526 5.48371L15.6897 10.127Z" fill="#A2ABB8" />
                    <path d="M20.8999 21.0175L7.78057 4.20989C7.59106 3.96711 7.30026 3.8252 6.99228 3.8252H3.50483C3.08836 3.8252 2.85443 4.30455 3.11069 4.63285L16.23 21.4405C16.4195 21.6833 16.7103 21.8252 17.0183 21.8252H20.5057C20.9222 21.8252 21.1561 21.3458 20.8999 21.0175Z" fill="#A2ABB8" />
                  </svg>
                </a>
                <a href="#" title="Youtube">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M5.58971 5.03461C3.72849 5.16074 2.29215 6.64213 2.1711 8.50369C2.08072 9.89371 2 11.5048 2 12.8252C2 14.1456 2.08072 15.7567 2.1711 17.1467C2.29215 19.0083 3.72849 20.4896 5.58971 20.6158C7.21433 20.7259 9.40027 20.8252 12 20.8252C14.5997 20.8252 16.7857 20.7259 18.4103 20.6158C20.2715 20.4896 21.7078 19.0083 21.8289 17.1467C21.9193 15.7567 22 14.1456 22 12.8252C22 11.5048 21.9193 9.89371 21.8289 8.50369C21.7078 6.64213 20.2715 5.16074 18.4103 5.03461C16.7857 4.92451 14.5997 4.8252 12 4.8252C9.40027 4.8252 7.21433 4.92451 5.58971 5.03461ZM11.5521 9.99299C10.8873 9.55284 10 10.0295 10 10.8268V14.8239C10 15.6211 10.8873 16.0978 11.552 15.6577L14.5454 13.6759C15.1546 13.2726 15.1547 12.3783 14.5455 11.975L11.5521 9.99299Z" fill="#A2ABB8" />
                  </svg>
                </a>
                <a href="#" title="Messenger">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 22.8252C17.5228 22.8252 22 18.348 22 12.8252C22 7.30235 17.5228 2.8252 12 2.8252C6.47715 2.8252 2 7.30235 2 12.8252C2 15.0764 2.74389 17.1539 3.99927 18.8252L3.99977 21.596C3.99989 22.2301 4.58261 22.704 5.20344 22.5749L8 21.9931C9.22492 22.5283 10.5778 22.8252 12 22.8252ZM6.70509 14.5322L9.45568 10.6028C9.76078 10.1669 10.3537 10.0463 10.8049 10.3283L13.4525 11.983C13.7858 12.1913 14.2102 12.1851 14.5372 11.9671L16.7092 10.519C16.8783 10.4063 17.1034 10.4286 17.2471 10.5723C17.3934 10.7186 17.4135 10.9487 17.2949 11.1182L14.5443 15.0476C14.2392 15.4835 13.6463 15.6041 13.1951 15.3221L10.5475 13.6674C10.2142 13.4591 9.78981 13.4653 9.4628 13.6833L7.29077 15.1313C7.1217 15.2441 6.89657 15.2218 6.75289 15.0781C6.60663 14.9318 6.58647 14.7017 6.70509 14.5322Z" fill="#A2ABB8" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="footer-contact color-secondary">
              <div className="fs-18">Roam Pays App LLC</div>
              <a href="mailto:eric@roampays.com" className="color-secondary fs-16 line-height-1" target="_blank" rel="noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                eric@roampays.com
              </a>
              <a href="tel:+17014047593" className="color-secondary fs-16 line-height-1" target="_blank" rel="noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                (701) 404-7593
              </a>
              <div className="fs-16 line-height-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                3003 32ND Ave S Ste 240, Fargo, ND 58103-6118
              </div>
            </div>
          </div>

          <div className="footer-links">
            <div>
              <h6>Links</h6>
              <a href="/#features" className="fs-14 color-secondary">Features</a>
              <a href="/#resources" className="fs-14 color-secondary">Resources</a>
            </div>
            <div>
              <h6>Company</h6>
              <a href="/#about" className="fs-14 color-secondary">About</a>
              <a href="/#contact" className="fs-14 color-secondary">Contact</a>
            </div>
            <div>
              <h6>Support</h6>
              <a href="/#help" className="fs-14 color-secondary">Help Center</a>
              <a href="/terms" className="fs-14 color-secondary">Terms</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom fs-14 fs-helvetica-neue">
          <div className="footer-bottom-left">
            <a href="/terms" className="color-secondary">Terms & Conditions</a>
            <a href="/privacy" className="color-secondary">Privacy Policy</a>
          </div>
          <div className="footer-bottom-right color-secondary">
            Copyright © {new Date().getFullYear()} | Roam. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}